import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ProductFiltersFormValues } from "../filters/ProductFilterFields.js";
import { useProductTypeInput_PimSearchProductTypesQuery } from "../ProductTypeInput.generated.js";
import { ProductSearchModal } from "./ProductSearchModal.js";

interface Props {
  productTypeKey: string;
  /**
   * Widens the search with the caller's preferred articles, as a placeholder search does, so
   * the result shows what the preferences make reachable.
   */
  includePreferences?: boolean;
  handleClose: () => void;
}

/**
 * The product search opened to set preferences for one product type, not to pick from.
 *
 * The product type is the only filter carried in, and it stays removable: the preference is
 * keyed to the product type the caller pinned, so widening the search only widens the
 * candidates — an article the catalogue has not classified yet can be preferred here.
 */
export function ProductPreferencesSearchModal({
  productTypeKey,
  includePreferences = false,
  handleClose,
}: Props) {
  const { t } = useTranslate(["Product"]);
  const client = useApolloClient();

  const query = useProductTypeInput_PimSearchProductTypesQuery({
    client,
    variables: {
      productTypeKeys: [productTypeKey],
      productTypeSystem: "REFERENCE",
    },
  });
  const productTypeLabel =
    getDataOrNull(query.data?.pimSearchProductTypes)?.productTypes?.[0]
      ?.label ?? null;

  const initialFilters = React.useMemo(
    (): Partial<ProductFiltersFormValues> => ({
      productTypeKeys: [productTypeKey],
    }),
    [productTypeKey]
  );

  return (
    <ProductSearchModal
      title={
        productTypeLabel
          ? t("Preferences for {productType}", {
              ns: "Product",
              productType: productTypeLabel,
            })
          : t("Product preferences", { ns: "Product" })
      }
      initialFilters={initialFilters}
      pinnedPreferenceProductTypeKey={productTypeKey}
      canChangeProductType
      includePreferences={includePreferences}
      handleClose={handleClose}
    />
  );
}
